import dayjs, { Dayjs } from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { isLeft } from "fp-ts/lib/Either"
import * as t from "io-ts"
import { PathReporter } from "io-ts/lib/PathReporter"
import { SnackbarKey, enqueueSnackbar } from "notistack"

import { DATA_DISPLAY_FALLBACK } from "../../constants/generalConstants"
import { AppError } from "../../errors/errorProcessors"

/**
 * Returns the original object if the condition is true, otherwise returns an empty object
 * @param {boolean} condition  - Test for whether to return the supplied object
 * @param {Object} obj - An object that may be returned
 * @returns {Object}
 */
export const objectOnCondition = (
    condition: boolean,
    obj: { [key: string]: unknown }
): { [key: string]: unknown } => (condition ? obj : {})

/**
 * Returns the original array if the condition is true, otherwise returns an empty array
 * @param {boolean} condition  - Test for whether to return the supplied array
 * @param {Array} array - An array that may be returned
 * @returns {Array}
 */
export const arrayOnCondition = <T>(condition: boolean, array: T[]): T[] =>
    condition ? array : []

/**
 * Formats datetime to dd/mm/yyyy, handling null values
 * @returns {string}
 */
export const formatDateTimeToDate = (date: string): string => {
    if (date) {
        const dateObj = new Date(date)
        const day = dateObj.getDate()
        const month = dateObj.getMonth() + 1
        const year = dateObj.getFullYear()
        return `${day}/${month}/${year}`
    }
    return date
}

/**
 * Helper for throwing snackbar notifications
 * @param {string} msg - snackbar message
 * @param {"success" | "error" | "info"} variant - defines type of snackbar
 */
export const snackbar = (
    msg: string,
    variant: "success" | "error" | "warning" | "default"
): SnackbarKey => enqueueSnackbar(msg, { variant })

/**
 * Used to process any unknown text prior to display. Adds fallback text if
 * data is null or undefined. Parses booleans into readable format.
 * @param { string | number | boolean | null | undefined } - text to process
 * @returns {string | number } - text processed and in format ready
 * to present to user
 */
export const displayText = (
    text: string | number | boolean | null | undefined
): string | number => {
    if (text === true) {
        return "Yes"
    }
    if (text === false) {
        return "No"
    }
    if (text === 0) {
        return "0"
    }
    if (text === undefined || text === null || Number.isNaN(text)) {
        return DATA_DISPLAY_FALLBACK
    }
    return text
}

/**
 * Ensures that the value is a string. If not, returns the backup value.
 * @param {unknown} value - value to be checked
 * @param {string} fallback - backup value to be returned if value is not a string
 * @returns {string} - either the value or the backup
 */
export const ensureString = (value: unknown, fallback: string): string =>
    typeof value === "string" ? value : fallback

/**
 * Ensures that the value is a string. If not, returns the backup value.
 * This sets a UI wide standard for handling dates.
 * @param {string} date - date to be checked
 */
export const formatDate = <T>(date: T): string => {
    if (!date) {
        return "Invalid Date"
    }
    const formattedDate = dayjs(String(date)).format("D MMM YY")
    return formattedDate === "Invalid Date" ? String(date) : formattedDate
}

/**
 * Ensures that the value is a string. If not, returns the backup value.
 * This sets a UI wide standard for handling datetimes.
 * @param {string} date - date to be checked
 */
export const formatDateTime = <T>(date: T): string => {
    if (!date) {
        return "Invalid Date Time"
    }
    const formattedDate = dayjs(String(date)).format("D MMM YY, HH:mm")
    return formattedDate === "Invalid Date" ? String(date) : formattedDate
}

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Ensures that the value is a string. If not, returns the backup value.
 * This sets a UI wide standard for handling times.
 * @param {string} time - time to be checked
 */
export const formatUnixDate = (unixTime: number): string => {
    if (!unixTime) {
        return "Invalid Date"
    }
    const formattedDate = dayjs.unix(unixTime).format("D MMM YY")
    return formattedDate === "Invalid Date" ? String(unixTime) : formattedDate
}

/**
 * Ensures that the value is a string. If not, returns the backup value.
 * This sets a UI wide standard for handling times.
 * @param {number} unixTime - Unix timestamp to be checked
 */
export const formatUnixDateTime = (unixTime: number): string => {
    if (!unixTime) {
        return "Invalid Date"
    }
    const formattedDate = dayjs
        .unix(unixTime)
        .tz(dayjs.tz.guess())
        .format("D MMM YY HH:mm")
    return formattedDate === "Invalid Date" ? String(unixTime) : formattedDate
}

const hasKey = <O extends object>(obj: O, key: PropertyKey): key is keyof O =>
    key in obj

export const getValue = <O extends object>(
    obj: O,
    key: PropertyKey
): O[keyof O] | null => {
    if (hasKey(obj, key)) {
        return obj[key]
    }
    return null
}

/**
 * React hook form parses all values as string in validated radio controls.
 * This function converts string "true" and "false" to their boolean equivalents.
 * If the value is neither "true" nor "false", the original value is returned.
 * If the value is a number, it is returned as a number.
 *
 * @param {string} strValue - The string to convert.
 * @returns {string | boolean} - The converted boolean value or the original string.
 */
export const parseReactHookFormRadioString = (
    strValue: string
): string | boolean | number => {
    if (strValue === "true") {
        return true
    }
    if (strValue === "false") {
        return false
    }
    if (Number(strValue)) {
        return Number(strValue)
    }
    return strValue
}

export const bytesToMb = (bytes: number): number => bytes / (1024 * 1024)

export const tsIoValidate = <T>(
    type: t.Type<T, unknown>,
    response: unknown,
    errorMsg: string
): T => {
    const decoded = type.decode(response)
    if (isLeft(decoded)) {
        const detail = `${errorMsg}: ${PathReporter.report(decoded).join("\n")}`
        throw new AppError(
            errorMsg,
            400,
            detail,
            "An invalid data schema was detected",
            "error"
        )
    }
    return decoded.right
}

export const getTsIoMapper = <T>(
    type: t.Type<T, unknown>,
    errorMsg: string
): {
    map: (response: unknown) => T
} => ({
    map: (response: unknown): T => tsIoValidate(type, response, errorMsg),
})

/**
 * Converts a Dayjs object to a string in the format required by the DNO API
 * @param {Dayjs} date - The Dayjs object to convert
 * @returns {string} - The converted string
 */
export const dayJsToStrForDnoApi = (date: Dayjs): string =>
    date.format("YYYY-MM-DD")

/**
 * Converts a Dayjs object to a string in the format required by the TTR API
 * @param {Dayjs} date - The Dayjs object to convert
 * @returns {string} - The converted string
 */
export const dayJsToStrForTtrApi = (date: Dayjs): string =>
    date.format("YYYY-MM-DDTHH:mm:ss")

/**
 * Converts a string in the format returned by the DNO API to a Dayjs object
 * @param {string} date - The string to convert
 * @returns {Dayjs} - The converted Dayjs object
 */
export const apiDateTimeToDayJs = (date: string): Dayjs | null =>
    date ? dayjs(date) : null

/**
 * Generates no options text for the Autocomplete component
 * @param searchTerm - The current search term
 * @param notStartedText - The text to display when the search term is empty
 * @param keepTypingText - The text to display when the search term is between 1 and 5 characters
 * @param noOptionFoundText - The text to display when no options are found
 * @returns - The generated text
 */
export const noOptionsText = (
    searchTerm: string,
    notStartedText: string,
    keepTypingText: string,
    noOptionFoundText: string
): string => {
    if (searchTerm === "") {
        return notStartedText
    }
    if (searchTerm.length >= 1 && searchTerm.length < 6) {
        return keepTypingText
    }
    return noOptionFoundText
}
type ValidIndexTypes = string | number | symbol

/**
 * Reverses an objects mapping, keys (K) and values (V) are swapped around.
 * K and V must be of type ValidIndexTypes
 *
 * @param {Record<K, V>} obj - The object to reverse
 * @returns {Record<V, K>} - The reversed object
 */
export const reverseObject = <
    K extends ValidIndexTypes,
    V extends ValidIndexTypes,
>(
    obj: Record<K, V>
): Record<V, K> => {
    const reverse: Partial<Record<V, K>> = {}

    const keys: K[] = Object.keys(obj) as Array<K>

    keys.forEach((key) => {
        const value: V = obj[key]
        reverse[value] = key
    })

    return reverse as Record<V, K>
}

/**
 * Checks if two arrays are identical.
 *
 * @param {unknown[]} arr1 - The first array to compare.
 * @param {unknown[]} arr2 - The second array to compare.
 * @returns {boolean} - Returns true if the arrays are identical, false otherwise.
 */
export const arraysAreIdentical = (
    arr1: unknown[],
    arr2: unknown[]
): boolean => {
    if (arr1.length !== arr2.length) {
        return false
    }
    for (let i = 0; i < arr1.length; i += 1) {
        if (arr1[i] !== arr2[i]) {
            return false
        }
    }
    return true
}

/**
 * Returns a new object, similar to the provided object with the specified property removed
 *
 * @param propKey - The name of the property to remove
 * @param param1 - The object the remove the property from
 * @returns Returns a new object with the property removed
 */
const removeProperty = (
    propKey: string | undefined,
    { [propKey ?? ""]: propValue, ...rest }
): typeof rest => rest

/**
 * Returns a new object, similar to the provided object with the specified properties removed
 * @param object - The object the remove the properties from
 * @param keys - The name of the properties to remove
 * @returns Returns a new object with the properties removed
 */
export const removeProperties = (
    object: any,
    ...keys: string[]
): Record<string, any> =>
    keys.length
        ? removeProperties(removeProperty(keys.pop(), object), ...keys)
        : object

/**
 * Removes properties with `undefined` or `null` values from an object.
 * @param {T} obj - The object from which to remove the properties.
 * @returns {T} The original object with `undefined` or `null` properties removed.
 */
export const removeUndefinedAndNulls = <T extends object>(obj: T): T =>
    Object.keys(obj).reduce((acc, key) => {
        const val = getValue(obj, key)
        if (val === null || val === undefined || val === "") {
            return acc // Skip null/undefined values
        }
        return { ...acc, [key]: val } // Accumulate non-null/undefined values
    }, {} as T)

/**
 * Applies display flex styling to an ApexCharts legend container.
 * Used as a chart event callback (mounted/updated) to ensure
 * the legend is displayed as a centered flex container.
 * @param chart - The ApexCharts instance with an optional el property
 */
export const applyLegendFlex = (chart: { el?: Element | null }): void => {
    // Defer DOM manipulation to ensure ApexCharts has finished rendering all legend items
    requestAnimationFrame(() => {
        const legend = chart.el?.querySelector(
            ".apexcharts-legend"
        ) as HTMLElement | null
        if (legend) {
            legend.style.display = "flex"
            legend.style.justifyContent = "center"
            legend.style.flexWrap = "wrap"

            // select children of the legend (the individual series items) and set them to display flex as well
            const legendSeries = legend.querySelectorAll(
                ".apexcharts-legend-series"
            )
            if (legendSeries) {
                legendSeries.forEach((el) => {
                    // eslint-disable-next-line no-param-reassign
                    ;(el as HTMLElement).style.display = "flex"
                })
            }
        }
    })
}
